import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ApplicationContext } from "../../context/GlobalState";
import { t, extractLocaleFromPath } from "../../i18n";
import { FaArrowRight } from "react-icons/fa";
import HreflangTags from "../../components/HreflangTags/HreflangTags";
import gaHero from "../../assets/images/heroes/ga-hero-main.webp";
import ncHero from "../../assets/images/heroes/nc-hero-main.webp";
import serviceCarAccident from "../../assets/images/services/service-car-accident.webp";
import serviceOrthopedics from "../../assets/images/services/service-orthopedics.webp";
import servicePersonalInjury from "../../assets/images/services/service-personal-injury.webp";
import servicePhysicalTherapy from "../../assets/images/services/service-physical-therapy.webp";
import servicePainManagement from "../../assets/images/services/service-pain-management.webp";
import serviceNeurology from "../../assets/images/services/service-neurology.webp";
import "./Injuries.css";

const BRAND_HEROES = { gso: gaHero, "apex-nc": ncHero, "apex-tn": ncHero };

const injuryCauses = [
  { title: "Car Accidents", slug: "car-accident-doctor", image: serviceCarAccident },
  { title: "Truck Accidents", slug: "truck-accident-doctor", image: serviceCarAccident },
  { title: "Motorcycle Accidents", slug: "motorcycle-accident-doctor", image: serviceCarAccident },
  { title: "Commercial Vehicle Accidents", slug: "truck-accident-doctor", image: serviceCarAccident },
  { title: "Uber & Rideshare Accidents", slug: "uber-accident-injury-treatment", image: serviceCarAccident },
  { title: "Pedestrian Accidents", slug: "pedestrian-accident-treatment", image: servicePersonalInjury },
  { title: "Workplace Accidents", slug: "workplace-injury-doctor", image: servicePersonalInjury },
];

const bodyPartInjuries = [
  { title: "Back & Spine Injuries", desc: "Herniated discs, sciatica, spinal stenosis", image: serviceOrthopedics, slug: "back-injury" },
  { title: "Neck Injury", desc: "Whiplash, cervical strain, disc herniation", image: serviceOrthopedics, slug: "neck-injury" },
  { title: "Shoulder Injury", desc: "Rotator cuff tears, dislocations, impingement", image: servicePhysicalTherapy, slug: "shoulder-injury" },
  { title: "Knee Injury", desc: "ACL tears, meniscus damage, ligament sprains", image: servicePhysicalTherapy, slug: "knee-injury" },
  { title: "Hip Injury", desc: "Fractures, bursitis, labral tears", image: servicePainManagement, slug: "hip-injury" },
  { title: "Low Back Injury", desc: "Lumbar disc herniation, sciatica, strain", image: servicePainManagement, slug: "low-back-injury" },
  { title: "Ankle Injury", desc: "Sprains, fractures, tendon tears", image: servicePhysicalTherapy, slug: "ankle-injury" },
  { title: "Elbow Injury", desc: "Tennis elbow, fractures, nerve compression", image: serviceOrthopedics, slug: "elbow-injury" },
  { title: "Hand Injury", desc: "Fractures, carpal tunnel, tendon injuries", image: serviceOrthopedics, slug: "hand-injury" },
  { title: "Foot Injury", desc: "Plantar fasciitis, stress fractures, sprains", image: servicePhysicalTherapy, slug: "foot-injury" },
  { title: "Head Injury", desc: "Concussions, TBI, post-concussion syndrome", image: serviceNeurology, slug: "head-injury" },
  { title: "Spine Injury", desc: "Compression fractures, spinal cord trauma", image: serviceOrthopedics, slug: "spine-injury" },
  { title: "Broken Bones", desc: "Fractures and orthopedic trauma", image: serviceOrthopedics, slug: "broken-bones" },
  { title: "Whiplash Injury", desc: "Cervical strain from auto collisions", image: serviceCarAccident, slug: "whiplash-injury" },
  { title: "Sports Injuries", desc: "ACL tears, rotator cuff, concussions", image: servicePhysicalTherapy, slug: "sports-injury" },
  { title: "Work Injuries", desc: "Repetitive strain, falls, lifting injuries", image: servicePersonalInjury, slug: "work-injury" },
];

export default function Injuries() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const locale = extractLocaleFromPath(location.pathname);
  const brand = state.brand || state.client;
  const brandKey = brand.brandKey || "gso";
  const locations = state.taxonomy.dimensions.find((d) => d.id === "location")?.values || [];
  const brandLocations = locations.filter((l) => l.brand === brandKey);
  const firstLoc = brandLocations[0] || locations[0];
  const heroImg = BRAND_HEROES[brandKey] || gaHero;
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const injuriesSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: `Injuries We Treat | ${brand.name}`,
    url: `https://${baseDomain}/injuries`,
    description: `${brand.name} treats back injuries, auto accident injuries, work injuries, joint pain, broken bones, and neurological conditions.`,
    about: [...injuryCauses, ...bodyPartInjuries].map(i => ({
      "@type": "MedicalCondition",
      name: i.title
    }))
  };

  return (
    <div className="injuries-page">
      <Helmet>
        <title>Injuries We Treat | {brand.name}</title>
        <meta name="description" content={`${brand.name} treats back injuries, auto accident injuries, work injuries, joint pain, broken bones, and neurological conditions. Free consultation. Call ${brand.phone}.`} />
        <meta property="og:title" content={`Injuries We Treat | ${brand.name}`} />
        <meta property="og:description" content={`Expert injury treatment at ${brand.name}. Orthopedic, spine, and accident injury care.`} />
        <meta property="og:url" content={`https://${baseDomain}/injuries`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={`https://${baseDomain}/injuries`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/injuries" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(injuriesSchema) }} />

      <section
        className="injuries-hero"
        style={{
          backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <h1>Injuries We Treat</h1>
          <p>Comprehensive care for all types of orthopedic and injury conditions at {brand.name}</p>
        </div>
      </section>

      {/* Injury Causes */}
      <section className="section-padding">
        <div className="container">
          <h2 className="section-title">Common Injury Causes</h2>
          <p className="section-subtitle">We treat injuries from all types of accidents and incidents</p>
          <div className="cause-grid">
            {injuryCauses.map((cause, i) => (
              <Link key={i} to={`/${firstLoc?.slug || "atlanta"}/${cause.slug}`} className="cause-card glass-card">
                <div className="cause-card-img">
                  <img src={cause.image} alt={cause.title} loading="lazy" />
                </div>
                <span className="cause-card-title">{cause.title}</span>
                <FaArrowRight className="cause-arrow" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Body Part Injuries */}
      <section className="section-padding bg-alt">
        <div className="container">
          <h2 className="section-title">Injuries by Body Part</h2>
          <p className="section-subtitle">Expert treatment for injuries throughout the body</p>
          <div className="inj-grid">
            {bodyPartInjuries.map((inj, i) => (
              <Link key={i} to={`/injuries/${inj.slug}`} className="inj-card glass-card">
                <div className="inj-card-image">
                  <img src={inj.image} alt={inj.title} loading="lazy" />
                </div>
                <div className="inj-card-content">
                  <h3>{inj.title}</h3>
                  <p>{inj.desc}</p>
                  <span className="inj-learn-more">{t("cta.learnMore", locale)} <FaArrowRight /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
